// Small DOM helpers. Every control is text (design §15, D-026).

type Attrs = Record<string, string | number | boolean | undefined | null>;
type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k === 'class') e.className = String(v);
    else if (k === 'text') e.textContent = String(v);
    else if (v === true) e.setAttribute(k, '');
    else e.setAttribute(k, String(v));
  }
  for (const c of children) if (c !== null && c !== undefined && c !== false) e.append(c);
  return e;
}

export function button(label: string, onClick: () => void, attrs: Attrs = {}): HTMLButtonElement {
  const b = el('button', { type: 'button', ...attrs }, label);
  b.addEventListener('click', onClick);
  return b;
}

/** One choice in a menu; a line above it when `apart`. */
export interface MenuItem {
  label: string;
  run: () => void;
  title?: string;
  apart?: boolean;
}

/**
 * A button that opens a list of text choices under it (design §15.1l). The
 * choices are asked for each time it opens, so they follow what the viewer may
 * do. A click elsewhere, Escape or a choice closes it.
 */
export function menuButton(label: string, items: () => MenuItem[], attrs: Attrs = {}): HTMLElement {
  const toggle = el('button', { type: 'button', 'aria-haspopup': 'menu', 'aria-expanded': 'false', ...attrs }, label);
  const list = el('div', { class: 'menu-list', role: 'menu', hidden: true });
  const root = el('div', { class: 'menu' }, toggle, list);
  const close = (refocus: boolean) => {
    if (list.hidden) return;
    list.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', outside, true);
    if (refocus) toggle.focus();
  };
  const outside = (e: PointerEvent) => {
    if (!root.contains(e.target as Node)) close(false);
  };
  toggle.addEventListener('click', () => {
    if (!list.hidden) return close(false);
    list.replaceChildren(...items().map((it) => button(it.label, () => {
      close(false);
      it.run();
    }, { role: 'menuitem', class: it.apart ? 'apart' : '', title: it.title })));
    list.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', outside, true);
    (list.firstElementChild as HTMLElement | null)?.focus();
  });
  // Arrows move between the choices; the map's keyboard shortcuts wait while the menu is open.
  root.addEventListener('keydown', (e) => {
    if (list.hidden) return;
    const choices = [...list.querySelectorAll('button')];
    const at = choices.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'Escape') close(true);
    else if (e.key === 'ArrowDown') choices[(at + 1) % choices.length]?.focus();
    else if (e.key === 'ArrowUp') choices[(at - 1 + choices.length) % choices.length]?.focus();
    else if (e.key === 'Tab') close(false);
    // Enter and Space press the focused choice as usual.
    if (e.key === 'Escape' || e.key === 'ArrowDown' || e.key === 'ArrowUp') e.preventDefault();
    e.stopPropagation();
  });
  return root;
}

export function select(options: [string, string][], value: string, onChange: (v: string) => void, attrs: Attrs = {}): HTMLSelectElement {
  const s = el('select', attrs);
  for (const [v, label] of options) s.append(new Option(label, v));
  s.value = value;
  s.addEventListener('change', () => onChange(s.value));
  return s;
}

/** A labelled row for forms in the side panel. */
export function field(label: string, control: Node, note?: string | Node): HTMLElement {
  return el('div', { class: 'field' }, el('label', { class: 'field-label' }, label), el('div', { class: 'field-control' }, control), note ? el('div', { class: 'field-note' }, note) : null);
}

/**
 * A modal dialog with text (or a form) and buttons; resolves to the chosen
 * value, or null when dismissed. Enter in a text field picks the first choice.
 */
export function dialog<T extends string>(title: string, text: string | Node, choices: [T, string][], cancelLabel = 'Cancel'): Promise<T | null> {
  return new Promise((resolve) => {
    const backdrop = el('div', { class: 'dialog-backdrop' });
    const close = (v: T | null) => {
      backdrop.remove();
      document.removeEventListener('keydown', onKey, true);
      resolve(v);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(null);
      } else if (e.key === 'Enter' && (e.target as HTMLElement | null)?.tagName === 'INPUT' && choices.length) {
        e.preventDefault();
        e.stopPropagation();
        close(choices[0]![0]);
      }
    };
    const box = el(
      'div',
      { class: 'dialog', role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
      el('h2', {}, title),
      typeof text === 'string' ? el('p', {}, text) : text,
      el('div', { class: 'dialog-buttons' }, ...choices.map(([v, label], i) => button(label, () => close(v), i === 0 ? { class: 'primary' } : {})), button(cancelLabel, () => close(null))),
    );
    backdrop.append(box);
    document.body.append(backdrop);
    document.addEventListener('keydown', onKey, true);
    ((box.querySelector('input:not([type="checkbox"]), select, textarea') ?? box.querySelector('button')) as HTMLElement | null)?.focus();
  });
}

export function isTyping(e: Event): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
}
